import { View, Text, Pressable, Image, Platform } from "react-native";
import {
  Heart,
  MapPin,
  Pencil,
  Shield,
  ShieldCheck,
  Video,
  Globe,
  Sparkles,
  DollarSign,
  Building2,
  Award,
  type LucideIcon,
} from "lucide-react-native";
import type { Organization } from "@louise/providers/providers.types";
import { CLINIC_SETTING_LABELS } from "@louise/providers/providers.types";
import {
  getClinicDecisionSupport,
  getOrganizationCountryLabel,
  type ClinicDecisionContext,
  type InsuranceMatchStatus,
} from "@louise/providers/hooks/useOrganizations";
import { EMBER } from "../../onboarding/theme";
import { IconButton, Tag, cardSurface, type TagTone } from "@louise/moon-ui";
import { getClinicFacilityOrLogoImages } from "./clinic/clinicImagePolicy";
import { useTranslations } from "@src/packages/i18n";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors, gradients } from "@louise/theme/tokens";
import { LinearGradient } from "expo-linear-gradient";
const clinicPlaceholder =
  require("../../assets/images/clinic-placeholder.jpg") as number;

type Props = {
  organization: Organization;
  onPress: (org: Organization) => void;
  distance?: string | null;
  insuranceMatch?: InsuranceMatchStatus;
  isSaved?: boolean;
  hasNotes?: boolean;
  onToggleSave?: (orgId: string) => void;
  homeCountry?: string;
  decisionContext?: ClinicDecisionContext;
};

function countryLabel(country: string) {
  return getOrganizationCountryLabel(country);
}

export default function MoonClinicCard({
  organization: org,
  onPress,
  distance,
  insuranceMatch,
  isSaved,
  hasNotes,
  onToggleSave,
  homeCountry,
  decisionContext,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("MoonClinicCard");
  const cityState = [org.address.city, org.address.state]
    .filter(Boolean)
    .join(" · ");

  const heroImage = getClinicFacilityOrLogoImages(org.images)[0];
  const networkName = org.network?.name;
  const decision = getClinicDecisionSupport(org, decisionContext);
  const isInternational =
    !!homeCountry &&
    !!org.address.country &&
    org.address.country !== homeCountry;

  const nationalRank = org.outcomes?.nationalRank;
  const showTopBadge =
    org.address.country === "FR" && nationalRank != null && nationalRank <= 10;

  // Smart tags: only compact decision info, max 4
  const tags: { label: string; tone: TagTone; icon?: LucideIcon }[] = [];
  tags.push({
    label: `${decision.matchPercent}% match`,
    tone: "blue",
    icon: Sparkles,
  });
  // Practice setting. Network clinics already surface their network name badge
  // over the hero image, so only boutique / hospital-based get a tag here.
  if (org.setting && org.setting !== "network-group") {
    tags.push({
      label: CLINIC_SETTING_LABELS[org.setting],
      tone: "neutral",
      icon: Building2,
    });
  }
  if (isInternational) {
    tags.push({
      label: countryLabel(org.address.country),
      tone: "blue",
      icon: Globe,
    });
  }
  if (insuranceMatch === "confirmed") {
    tags.push({
      label: t("access.clinic_card.your_insurance"),
      tone: "neutral",
      icon: ShieldCheck,
    });
  } else if (org.insurance?.accepted) {
    tags.push({
      label: t("access.clinic_card.insurance"),
      tone: "neutral",
      icon: Shield,
    });
  }
  const hasTelehealth = org.consultationTypes?.includes("telehealth");
  const hasInPerson = org.consultationTypes?.includes("in-person");
  if (hasTelehealth && hasInPerson) {
    tags.push({
      label: t("access.clinic_card.also_virtual"),
      tone: "neutral",
      icon: Video,
    });
  }
  if (decision.pricingLabel === "Price listed") {
    tags.push({
      label: "Price",
      tone: "neutral",
      icon: DollarSign,
    });
  }
  if (org.isFirstConsultationFree) {
    tags.push({
      label: t("access.clinic_card.free_consult"),
      tone: "coral",
    });
  }

  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(org.name), "moon_clinic_card");
        onPress(org);
      }}
      style={{
        ...cardSurface(),
        padding: 0,
        overflow: "hidden",
        flex: 1,
      }}
    >
      {/* Image / Placeholder */}
      <View
        style={{
          height: 90,
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Image
          source={heroImage ? { uri: heroImage.url } : clinicPlaceholder}
          style={{ width: "100%", height: "100%", position: "absolute" }}
          resizeMode="cover"
        />

        {/* Top-10 rank badge */}
        {showTopBadge && (
          <LinearGradient
            {...gradients.accent}
            style={{
              position: "absolute",
              top: 6,
              left: 6,
              flexDirection: "row",
              alignItems: "center",
              gap: 3,
              paddingHorizontal: 7,
              paddingVertical: 3,
              borderRadius: 8,
              ...(Platform.OS === "web" && {
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }),
            }}
          >
            <Award
              size={10}
              color={semanticColors.text.inverse}
              strokeWidth={2}
            />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 9,
                fontWeight: "700",
                color: semanticColors.text.inverse,
                letterSpacing: 0.3,
              }}
            >
              {t("access.clinic_card.rank_badge", {
                params: { rank: String(nationalRank) },
              })}
            </Text>
          </LinearGradient>
        )}

        {/* Save button */}
        {onToggleSave && (
          <IconButton
            accessibilityLabel={
              isSaved
                ? t("access.clinic_detail.journey.saved")
                : t("access.clinic_detail.journey.save")
            }
            action={isSaved ? "unsave_clinic" : "save_clinic"}
            onPress={(e) => {
              e.stopPropagation();
              onToggleSave(org.id);
            }}
            hitSlop={6}
            // Inert testability hooks: expose the save toggle as an
            // addressable button whose pressed state (aria-pressed) mirrors
            // isSaved, so E2E can assert the filled/unfilled heart. Purely
            // observational — does not affect behavior.
            aria-pressed={!!isSaved}
            testID="clinic-card-heart"
            size="xs"
            surface="bare"
            backgroundColor={semanticColors.overlay.lightScrim}
            pressedBackgroundColor={semanticColors.overlay.lightScrim}
            style={{
              position: "absolute",
              top: 6,
              right: 6,
              ...(Platform.OS === "web" && {
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }),
            }}
          >
            <Heart
              size={13}
              color={
                isSaved
                  ? EMBER.colors.coral
                  : semanticColors.onDark.text.primary
              }
              fill={isSaved ? EMBER.colors.coral : "transparent"}
              strokeWidth={2}
            />
          </IconButton>
        )}

        {/* Notes indicator */}
        {hasNotes && (
          <View
            style={{
              position: "absolute",
              top: 6,
              right: onToggleSave ? 38 : 6,
              width: 26,
              height: 26,
              borderRadius: 13,
              backgroundColor: semanticColors.overlay.lightScrim,
              alignItems: "center",
              justifyContent: "center",
              ...(Platform.OS === "web" && {
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }),
            }}
          >
            <Pencil
              size={11}
              color={semanticColors.onDark.text.primary}
              strokeWidth={2}
            />
          </View>
        )}

        {/* Network badge */}
        {networkName && (
          <View
            style={{
              position: "absolute",
              bottom: 8,
              left: 8,
              backgroundColor: semanticColors.overlay.scrim,
              paddingHorizontal: 7,
              paddingVertical: 3,
              borderRadius: 8,
              ...(Platform.OS === "web" && {
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }),
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 9,
                fontWeight: "600",
                color: semanticColors.onDark.text.primary,
                letterSpacing: 0.3,
              }}
            >
              {networkName}
            </Text>
          </View>
        )}
      </View>

      {/* Content */}
      <View style={{ padding: 14, gap: 6, flex: 1 }}>
        {/* Name */}
        <Text
          className="font-dm-sans"
          testID="clinic-card-name"
          style={{
            fontSize: 14,
            fontWeight: "700",
            color: EMBER.colors.text,
            lineHeight: 18,
          }}
          numberOfLines={2}
        >
          {org.name}
        </Text>

        {/* Location + distance */}
        {cityState ? (
          <View style={{ flexDirection: "row", alignItems: "center", gap: 3 }}>
            <MapPin
              size={10}
              color={EMBER.colors.textFaint}
              strokeWidth={1.5}
            />
            <Text
              className="font-dm-sans"
              style={{ fontSize: 11, color: EMBER.colors.textLight }}
              numberOfLines={1}
            >
              {cityState}
              {distance ? ` · ${distance}` : ""}
            </Text>
          </View>
        ) : null}

        {/* Spacer to push tags to bottom */}
        <View style={{ flex: 1 }} />

        {/* Smart tags */}
        {tags.length > 0 && (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 4,
              marginTop: 2,
            }}
          >
            {tags.slice(0, 4).map((tag) => (
              <Tag
                key={tag.label}
                label={tag.label}
                tone={tag.tone}
                icon={tag.icon}
                size="compact"
              />
            ))}
          </View>
        )}
      </View>
    </Pressable>
  );
}
